import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  ArrowLeft, FileSignature, Search, Plus, X, Loader2, Wrench, Package, User,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { navegarLista } from '@/lib/keyboard'

interface ItemCarrinho {
  produto_id: number
  descricao: string
  quantidade: number
  preco_unit: number
  desconto: number
  eh_servico: boolean
}

const VALIDADE_PADRAO = 7

function dataMais(dias: number): string {
  const d = new Date()
  d.setDate(d.getDate() + dias)
  return d.toISOString().slice(0, 10)
}

export default function NovoOrcamentoPage() {
  const navigate = useNavigate()
  const [tipo, setTipo] = useState<'venda' | 'servico'>('venda')
  const [cliente, setCliente] = useState<{ id: number; nome: string } | null>(null)
  const [buscaCliente, setBuscaCliente] = useState('')
  const [colaboradorId, setColaboradorId] = useState<number | ''>('')
  const [equipamentoId, setEquipamentoId] = useState<number | ''>('')
  const [descricaoProblema, setDescricaoProblema] = useState('')
  const [observacao, setObservacao] = useState('')
  const [validoAte, setValidoAte] = useState(dataMais(VALIDADE_PADRAO))
  const [desconto, setDesconto] = useState('0')
  const [itens, setItens] = useState<ItemCarrinho[]>([])
  const [buscaProduto, setBuscaProduto] = useState('')
  const [abrirBusca, setAbrirBusca] = useState(false)
  // Índice destacado nos dropdowns — reseta pra 0 a cada digitação
  const [hiCliente, setHiCliente] = useState(0)
  const [hiProduto, setHiProduto] = useState(0)

  const { data: clientes = [] } = useQuery<any[]>({
    queryKey: ['orc-clientes', buscaCliente],
    queryFn: async () => (await api.get('/cadastros/pessoas', {
      params: { q: buscaCliente, tipo: 'cliente', limit: 15 },
    })).data,
    enabled: buscaCliente.trim().length >= 2,
  })

  // Vendedor para orçamento de venda, técnico para orçamento de serviço.
  const { data: colaboradores = [] } = useQuery<any[]>({
    queryKey: ['orc-colaboradores', tipo],
    queryFn: async () => (await api.get('/cadastros/colaboradores', {
      params: { tipo: tipo === 'servico' ? 'tecnico' : 'vendedor', ativos: true },
    })).data,
  })

  const { data: bikes = [] } = useQuery<any[]>({
    queryKey: ['orc-bikes', cliente?.id],
    queryFn: async () => (await api.get('/servicos/equipamentos', {
      params: { cliente_id: cliente!.id },
    })).data,
    enabled: tipo === 'servico' && !!cliente,
  })

  const { data: produtos = [] } = useQuery<any[]>({
    queryKey: ['orc-produtos', buscaProduto],
    queryFn: async () => (await api.get('/cadastros/produtos', {
      params: { q: buscaProduto.trim() || undefined, limit: 20 },
    })).data,
    enabled: abrirBusca && buscaProduto.trim().length > 0,
  })

  // O dropdown agrupa serviços antes de produtos — a navegação por teclado
  // precisa seguir a MESMA ordem, senão a seta destaca um item e o Enter
  // seleciona outro.
  const produtosOrdenados = useMemo(() => [
    ...produtos.filter((p: any) => (p.tipo || 'produto') === 'servico'),
    ...produtos.filter((p: any) => (p.tipo || 'produto') !== 'servico'),
  ], [produtos])

  const subtotal = useMemo(
    () => itens.reduce((s, i) => s + (i.preco_unit * i.quantidade - i.desconto), 0),
    [itens])
  const total = Math.max(0, subtotal - (parseFloat(desconto) || 0))

  const adicionar = (p: any) => {
    setItens(prev => {
      const existe = prev.findIndex(i => i.produto_id === p.id)
      if (existe >= 0) {
        const novo = [...prev]
        novo[existe] = { ...novo[existe], quantidade: novo[existe].quantidade + 1 }
        return novo
      }
      return [...prev, {
        produto_id: p.id,
        descricao: p.descricao,
        quantidade: 1,
        preco_unit: Number(p.preco_venda || 0),
        desconto: 0,
        eh_servico: (p.tipo || '') === 'servico',
      }]
    })
    setBuscaProduto('')
    setAbrirBusca(false)
  }

  const alterar = (idx: number, campo: keyof ItemCarrinho, valor: number) =>
    setItens(prev => prev.map((it, i) => i === idx ? { ...it, [campo]: valor } : it))

  const salvar = useMutation({
    mutationFn: async () => (await api.post('/orcamentos', {
      tipo,
      cliente_id: cliente!.id,
      vendedor_cad_id: tipo === 'venda' ? (colaboradorId || null) : null,
      tecnico_cad_id: tipo === 'servico' ? (colaboradorId || null) : null,
      equipamento_id: tipo === 'servico' ? (equipamentoId || null) : null,
      descricao_problema: descricaoProblema.trim() || null,
      observacao: observacao.trim() || null,
      valido_ate: validoAte,
      desconto: parseFloat(desconto) || 0,
      itens: itens.map(i => ({
        produto_id: i.produto_id,
        quantidade: i.quantidade,
        preco_unit: i.preco_unit,
        desconto: i.desconto,
      })),
    })).data,
    onSuccess: (r: any) => {
      toast.success(`Orçamento #${r.numero} criado`)
      navigate(`/orcamentos/${r.id}`)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao criar orçamento'),
  })

  const pronto = !!cliente && itens.length > 0

  return (
    <div className="p-6 space-y-4 max-w-4xl mx-auto">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/orcamentos')} className="btn-secondary !px-2">
          <ArrowLeft size={16} />
        </button>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileSignature className="text-brand-600" /> Novo orçamento
        </h1>
      </div>

      <div className="card card-body space-y-4">
        <div>
          <div className="text-xs font-medium mb-1.5">O orçamento é de quê?</div>
          <div className="flex gap-2">
            {([['venda', 'Venda de produtos', Package],
               ['servico', 'Serviço na bike', Wrench]] as const).map(([v, l, I]) => (
              <button key={v} onClick={() => { setTipo(v); setColaboradorId('') }}
                className={`flex-1 rounded-lg border p-3 text-left transition-colors ${
                  tipo === v ? 'border-brand-500 bg-brand-50' : 'border-ink-200 hover:border-ink-300'}`}>
                <I size={16} className={tipo === v ? 'text-brand-600' : 'text-ink-400'} />
                <div className="font-medium text-sm mt-1">{l}</div>
                <div className="text-xs text-ink-500">
                  {v === 'venda' ? 'Ao aceitar, abre no PDV para cobrar'
                                 : 'Ao aceitar, vira uma Ordem de Serviço'}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs font-medium mb-1 flex items-center gap-1"><User size={12} /> Cliente</div>
          {cliente ? (
            <div className="flex items-center gap-2">
              <span className="flex-1 rounded-lg bg-ink-50 border border-ink-200 px-3 py-2 text-sm">
                {cliente.nome}
              </span>
              <button onClick={() => { setCliente(null); setEquipamentoId('') }}
                className="btn-secondary !px-2"><X size={14} /></button>
            </div>
          ) : (
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400" />
              <input value={buscaCliente}
                onChange={e => { setBuscaCliente(e.target.value); setHiCliente(0) }}
                onKeyDown={navegarLista(clientes, hiCliente, setHiCliente,
                  (p: any) => { setCliente({ id: p.id, nome: p.nome }); setBuscaCliente('') },
                  () => setBuscaCliente(''))}
                className="input w-full pl-8" placeholder="Digite o nome do cliente..." autoFocus />
              {buscaCliente.trim().length >= 2 && clientes.length > 0 && (
                <div className="absolute z-20 mt-1 w-full max-h-48 overflow-auto bg-white border border-ink-200 rounded-lg shadow-lg">
                  {clientes.map((p: any, i: number) => (
                    <button key={p.id} onClick={() => { setCliente({ id: p.id, nome: p.nome }); setBuscaCliente('') }}
                      onMouseEnter={() => setHiCliente(i)}
                      className={`w-full text-left px-3 py-1.5 text-sm truncate ${
                        i === hiCliente ? 'bg-brand-100' : 'hover:bg-brand-50'}`}>
                      {p.nome}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <div className="text-xs font-medium mb-1">{tipo === 'servico' ? 'Mecânico' : 'Vendedor'}</div>
            <select className="input w-full" value={colaboradorId}
              onChange={e => setColaboradorId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">Não informar</option>
              {colaboradores.map((c: any) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-xs font-medium mb-1">Válido até</div>
            <input type="date" className="input w-full" value={validoAte}
              onChange={e => setValidoAte(e.target.value)} />
          </label>
        </div>

        {tipo === 'servico' && cliente && (
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs font-medium mb-1">Bike</div>
              <select className="input w-full" value={equipamentoId}
                onChange={e => setEquipamentoId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">Nenhuma</option>
                {bikes.map((b: any) => (
                  <option key={b.id} value={b.id}>{b.descricao}{b.cor ? ` · ${b.cor}` : ''}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1">Serviço solicitado</div>
              <input className="input w-full" value={descricaoProblema}
                onChange={e => setDescricaoProblema(e.target.value.toUpperCase())}
                placeholder="Ex.: REVISÃO COMPLETA" />
            </label>
          </div>
        )}
      </div>

      <div className="card card-body space-y-3">
        <div className="text-sm font-semibold">Itens do orçamento</div>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400" />
          <input value={buscaProduto} autoComplete="off"
            onChange={e => { setBuscaProduto(e.target.value); setAbrirBusca(true); setHiProduto(0) }}
            onFocus={() => setAbrirBusca(true)}
            onKeyDown={navegarLista(produtosOrdenados, hiProduto, setHiProduto,
              (p: any) => adicionar(p), () => setAbrirBusca(false))}
            className="input w-full pl-8" placeholder="Buscar produto ou serviço para adicionar..." />
          {abrirBusca && buscaProduto.trim() && (
            <div className="absolute z-20 mt-1 w-full max-h-64 overflow-auto bg-white border border-ink-200 rounded-lg shadow-lg">
              {produtos.length === 0 ? (
                <div className="p-3 text-center text-sm text-ink-500">Nenhum produto encontrado</div>
              ) : (
                (['servico', 'produto'] as const).map(grupo => {
                  const lista = produtos.filter((p: any) => (p.tipo || 'produto') === grupo)
                  if (lista.length === 0) return null
                  return (
                    <div key={grupo}>
                      <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-ink-400 bg-ink-50 sticky top-0">
                        {grupo === 'servico' ? 'Serviços' : 'Produtos e peças'}
                      </div>
                      {lista.map((p: any) => {
                        const idx = produtosOrdenados.findIndex((x: any) => x.id === p.id)
                        return (
                        <button key={p.id} onClick={() => adicionar(p)}
                          onMouseEnter={() => setHiProduto(idx)}
                          className={`w-full px-3 py-2 text-left border-b last:border-0 flex items-center justify-between gap-2 ${
                            idx === hiProduto ? 'bg-brand-100' : 'hover:bg-brand-50'}`}>
                          <div className="min-w-0">
                            <p className="text-sm truncate" title={p.descricao}>{p.descricao}</p>
                            <span className="text-xs text-ink-500 font-mono">{p.codigo}</span>
                          </div>
                          <span className="text-sm font-semibold text-emerald-700 shrink-0">
                            {formatMoney(Number(p.preco_venda || 0))}
                          </span>
                        </button>
                        )
                      })}
                    </div>
                  )
                })
              )}
            </div>
          )}
        </div>

        {itens.length === 0 ? (
          <p className="text-sm text-ink-500 py-4 text-center">
            Nenhum item ainda. Busque acima para adicionar.
          </p>
        ) : (
          <table className="table text-sm">
            <thead>
              <tr>
                <th>Item</th>
                <th className="text-right w-24">Qtd</th>
                <th className="text-right w-32">Preço</th>
                <th className="text-right w-28">Total</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {itens.map((it, idx) => (
                <tr key={`${it.produto_id}-${idx}`} className="border-b">
                  <td>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] mr-1.5 ${
                      it.eh_servico ? 'bg-indigo-100 text-indigo-800' : 'bg-amber-100 text-amber-800'}`}>
                      {it.eh_servico ? 'Serviço' : 'Peça'}
                    </span>
                    <span title={it.descricao}>{it.descricao}</span>
                  </td>
                  <td>
                    <input type="number" min="0" step="0.001" value={it.quantidade}
                      onChange={e => alterar(idx, 'quantidade', parseFloat(e.target.value) || 0)}
                      className="input !py-1 text-sm text-right w-full" />
                  </td>
                  <td>
                    <input type="number" min="0" step="0.01" value={it.preco_unit}
                      onChange={e => alterar(idx, 'preco_unit', parseFloat(e.target.value) || 0)}
                      className="input !py-1 text-sm text-right w-full" />
                  </td>
                  <td className="text-right font-mono font-semibold">
                    {formatMoney(it.preco_unit * it.quantidade - it.desconto)}
                  </td>
                  <td className="text-right">
                    <button onClick={() => setItens(prev => prev.filter((_, i) => i !== idx))}
                      className="text-rose-600 hover:bg-rose-50 rounded p-1"><X size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="flex items-end justify-between gap-4 pt-2 border-t">
          <label className="block w-40">
            <div className="text-xs font-medium mb-1">Desconto no total</div>
            <input type="number" min="0" step="0.01" value={desconto}
              onChange={e => setDesconto(e.target.value)} className="input w-full text-right" />
          </label>
          <div className="text-right">
            <div className="text-xs text-ink-500">Subtotal {formatMoney(subtotal)}</div>
            <div className="text-2xl font-bold">{formatMoney(total)}</div>
          </div>
        </div>
      </div>

      <label className="block">
        <div className="text-xs font-medium mb-1">Observações (saem no orçamento impresso)</div>
        <textarea className="input w-full" rows={2} value={observacao}
          onChange={e => setObservacao(e.target.value)} />
      </label>

      <div className="flex justify-end gap-2">
        <button onClick={() => navigate('/orcamentos')} className="btn-secondary">Cancelar</button>
        <button onClick={() => salvar.mutate()} disabled={!pronto || salvar.isPending}
          className="btn-primary">
          {salvar.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Criar orçamento
        </button>
      </div>
    </div>
  )
}